import React, { useCallback, useEffect, useState } from 'react';
import { policyAPI, showApiError } from '../../services/api';
import {
  POLICY_OPTIONS,
  toDateTimeLocalValue,
  toIsoDateTime,
} from './policyRegistrationShared';
import './PolicyRegistration.css';

const PolicyEditorPage = ({ sourceId, policyType: initialPolicyType, onBack }) => {
  const [editorType, setEditorType] = useState(initialPolicyType || 'TERMS_OF_SERVICE');
  const [fromSourceId, setFromSourceId] = useState(sourceId ?? null);
  const [editingPolicyId, setEditingPolicyId] = useState(sourceId ?? null);
  const [title, setTitle] = useState('');
  const [version, setVersion] = useState('');
  const [content, setContent] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  const applyPolicyToForm = useCallback((data) => {
    setTitle(data.title || '');
    setVersion(data.version || '');
    setContent(data.content || '');
    setEffectiveDate(toDateTimeLocalValue(data.effectiveDate));
    setActive(data.active !== false);
  }, []);

  const resetForm = useCallback(() => {
    setTitle('');
    setVersion('');
    setContent('');
    setEffectiveDate('');
    setActive(true);
  }, []);

  const loadActivePolicy = useCallback(async (policyType) => {
    setLoading(true);
    try {
      const data = await policyAPI.getActivePolicy(policyType);
      setEditingPolicyId(data.id ?? null);
      applyPolicyToForm(data);
    } catch (error) {
      if (error?.response?.status !== 404) {
        showApiError(error, '활성 정책을 불러오지 못했습니다.');
      }
      setEditingPolicyId(null);
      resetForm();
    } finally {
      setLoading(false);
    }
  }, [applyPolicyToForm, resetForm]);

  const loadPolicyById = useCallback(async (id) => {
    setLoading(true);
    try {
      const data = await policyAPI.getPolicyById(id);
      if (data.policyType) {
        setEditorType(data.policyType);
      }
      setEditingPolicyId(data.id ?? null);
      applyPolicyToForm(data);
    } catch (error) {
      showApiError(error, '정책을 불러오지 못했습니다.');
      setEditingPolicyId(null);
      resetForm();
    } finally {
      setLoading(false);
    }
  }, [applyPolicyToForm, resetForm]);

  useEffect(() => {
    if (fromSourceId) {
      loadPolicyById(fromSourceId);
      return;
    }
    loadActivePolicy(editorType);
  }, [fromSourceId, editorType, loadPolicyById, loadActivePolicy]);

  const handleEditorTypeChange = (nextType) => {
    setFromSourceId(null);
    setEditingPolicyId(null);
    setEditorType(nextType);
  };

  const buildPayload = () => {
    const isoEffectiveDate = toIsoDateTime(effectiveDate);
    if (!isoEffectiveDate) {
      window.alert('시행일시 형식이 올바르지 않습니다.');
      return null;
    }

    return {
      title: title.trim(),
      version: version.trim() || null,
      content,
      effectiveDate: isoEffectiveDate,
      active,
    };
  };

  const validateForm = () => {
    if (!title.trim()) {
      window.alert('제목을 입력해 주세요.');
      return false;
    }
    if (!content.trim()) {
      window.alert('HTML 내용을 입력해 주세요.');
      return false;
    }
    if (!effectiveDate) {
      window.alert('시행일시를 입력해 주세요.');
      return false;
    }
    return true;
  };

  const handleUpdate = async () => {
    if (!editingPolicyId) return;
    if (!validateForm()) return;

    const payload = buildPayload();
    if (!payload) return;

    if (!window.confirm(`ID ${editingPolicyId} 정책을 수정합니다.\n새 버전은 추가되지 않습니다.`)) return;

    setSaving(true);
    try {
      await policyAPI.updatePolicy(editingPolicyId, payload);
      window.alert('정책이 수정되었습니다.');
      onBack();
    } catch (error) {
      showApiError(error, '정책 수정에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const payload = buildPayload();
    if (!payload) return;

    const activeLabel = active ? '활성' : '비활성';
    if (!window.confirm(`새 정책 버전으로 저장합니다. (${activeLabel})\n기존 버전은 그대로 유지됩니다.`)) return;

    setSaving(true);
    try {
      await policyAPI.createPolicy(editorType, payload);
      window.alert('새 정책이 저장되었습니다.');
      onBack();
    } catch (error) {
      showApiError(error, '정책 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const editorLabel = POLICY_OPTIONS.find((item) => item.type === editorType)?.label || '정책';

  return (
    <div className="policy-registration policy-registration-editor-view registration-section">
      <div className="policy-registration-toolbar">
        <button
          type="button"
          className="btn btn-secondary policy-back-button"
          onClick={onBack}
          disabled={saving}
        >
          ← 목록
        </button>

        <label htmlFor="policy-type-select">정책 종류</label>
        <select
          id="policy-type-select"
          value={editorType}
          onChange={(event) => handleEditorTypeChange(event.target.value)}
          disabled={loading || saving}
        >
          {POLICY_OPTIONS.map((option) => (
            <option key={option.type} value={option.type}>
              {option.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          className={`btn btn-secondary ${showPreview ? 'active' : ''}`}
          onClick={() => setShowPreview((prev) => !prev)}
          disabled={loading || saving}
        >
          {showPreview ? '미리보기 숨기기' : '미리보기 보기'}
        </button>
      </div>

      {loading ? (
        <div className="policy-registration-state">정책을 불러오는 중…</div>
      ) : (
        <>
          <div className="policy-registration-meta">
            <div className="policy-registration-field">
              <label htmlFor="policy-title">제목</label>
              <input
                id="policy-title"
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                disabled={saving}
              />
            </div>
            <div className="policy-registration-field">
              <label htmlFor="policy-version">버전</label>
              <input
                id="policy-version"
                type="text"
                value={version}
                onChange={(event) => setVersion(event.target.value)}
                placeholder="예: 1.0"
                disabled={saving}
              />
            </div>
            <div className="policy-registration-field">
              <label htmlFor="policy-effective-date">시행일시</label>
              <input
                id="policy-effective-date"
                type="datetime-local"
                value={effectiveDate}
                onChange={(event) => setEffectiveDate(event.target.value)}
                disabled={saving}
              />
            </div>
            <div className="policy-registration-field policy-registration-field-checkbox">
              <label htmlFor="policy-active">활성 여부</label>
              <label className="policy-active-toggle">
                <input
                  id="policy-active"
                  type="checkbox"
                  checked={active}
                  onChange={(event) => setActive(event.target.checked)}
                  disabled={saving}
                />
                <span>{active ? 'true · 노출' : 'false · 보관'}</span>
              </label>
            </div>
          </div>

          <p className="policy-registration-hint">
            목록에서 불러온 정책은 <strong>수정</strong>으로 덮어쓸 수 있습니다. 내용을 새로 남기려면 <strong>새 버전으로 저장</strong>을 사용하세요.
          </p>

          <div className={`policy-registration-editor ${showPreview ? 'with-preview' : ''}`}>
            <div className="policy-registration-panel">
              <div className="policy-registration-panel-header">
                <h3>HTML 편집</h3>
                <span>{editorLabel}</span>
              </div>
              <div className="policy-registration-panel-body">
                <textarea
                  className="policy-registration-textarea"
                  value={content}
                  onChange={(event) => setContent(event.target.value)}
                  placeholder="<div class=&quot;policy-document&quot;>...</div>"
                  disabled={saving}
                  spellCheck={false}
                />
              </div>
            </div>

            {showPreview && (
              <div className="policy-registration-panel">
                <div className="policy-registration-panel-header">
                  <h3>미리보기</h3>
                  <span>저장 전 확인</span>
                </div>
                <div className="policy-registration-panel-body">
                  <article
                    className="policy-content policy-html-content"
                    dangerouslySetInnerHTML={{ __html: content }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="policy-registration-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleUpdate}
              disabled={saving || !editingPolicyId}
              title={editingPolicyId ? `ID ${editingPolicyId} 수정` : '목록에서 정책을 불러오면 수정할 수 있습니다'}
            >
              {saving ? '처리 중…' : '수정'}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? '저장 중…' : '새 버전으로 저장'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PolicyEditorPage;
